import React from "react";
import { Box, Typography, Link } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const googlePlayImg =
  "https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg";
const appStoreImg =
  "https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg";

const columnTitleSx = {
  fontWeight: "bold",
  marginBottom: { xs: "6px", sm: "10px" },
  fontSize: { xs: "0.8rem", sm: "1rem", md: "1.1rem" },
  lineHeight: 1.2,
};

const columnLinkSx = {
  fontSize: { xs: "0.7rem", sm: "0.875rem", md: "1rem" },
  marginBottom: { xs: "5px", sm: "8px" },
  cursor: "pointer",
  display: "block",
  lineHeight: 1.4,
  wordBreak: "break-word",
};

const gridAreaSx = {
  brand: { gridArea: "brand", minWidth: 0 },
  company: { gridArea: "company", minWidth: 0 },
  help: { gridArea: "help", minWidth: 0 },
  account: { gridArea: "account", minWidth: 0 },
};

const Footer = () => {
  const customerId = useSelector((state) => state?.user?.customerId);
  const navigate = useNavigate();

  const handleAccountClick = (item) => {
    if (!customerId) {
      setTimeout(() => {
        navigate("/login");
      }, 200);
    } else {
      setTimeout(() => {
        navigate(`/profileinfo?tab=${item}`);
      }, 200);
    }
  };

  const goToAbout = () => {
    navigate("/about-us");
  };

  const goToTermsConditions = () => {
    navigate("/termsconditions");
  };

  const goToprivacypolicy = () => {
    navigate("/privacy-policy");
  };

  const goToVendorAgreement = () => {
    navigate("/vendor-agreement");
  };

  return (
    <Box
      component="footer"
      sx={{
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
        overflowX: "hidden",
        background:
          "linear-gradient(to bottom,rgb(249, 88, 43),rgb(213, 28, 8))",
        color: "white",
        py: { xs: 2.5, sm: 4, md: 5 },
        px: { xs: 1.5, sm: 3, md: 6, lg: 10 },
      }}
    >
      {/*
        Mobile: brand full width on top, 3 link columns below (no overlap).
        Tablet+: same 4-column row as desktop.
      */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(3, minmax(0, 1fr))",
            sm: "minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
            md: "minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
          },
          gridTemplateAreas: {
            xs: `"brand brand brand"
                 "company help account"`,
            sm: `"brand company help account"`,
          },
          gap: { xs: 2, sm: 2, md: 4 },
          rowGap: { xs: 2.5, sm: 2, md: 4 },
          alignItems: "flex-start",
          textAlign: "left",
        }}
      >
        {/* Column 1 — Brand */}
        <Box sx={gridAreaSx.brand}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
              lineHeight: 1.2,
            }}
          >
            Fast Delivery
          </Typography>
          {/* <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
              lineHeight: 1.1,
            }}
          >
            LOGO
          </Typography> */}

          <Box
            component="img"
            src="/Sthanick.jpeg"
            alt="Sthanick"
            sx={{
              height: 40,
              width: "auto",
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
              mt: { xs: 0.5, sm: 1 },
              lineHeight: 1.35,
              maxWidth: { xs: "100%", sm: "none" },
            }}
          >
            <Link
              href="/see-allcategorys"
              underline="none"
              color="inherit"
              sx={{ cursor: "pointer", wordBreak: "break-word" }}
            >
              Groceries, Electronics, Home Appliances, Vehicles
            </Link>
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5 },
              mt: { xs: 1.5, sm: 2 },
              maxWidth: "100%",
            }}
          >
            <Box
              component="img"
              src={googlePlayImg}
              alt="Get it on Google Play"
              sx={{
                width: { xs: 120, sm: 130, md: 150 },
                maxWidth: "calc(50% - 8px)",
                height: "auto",
                flexShrink: 0,
              }}
            />
            <Box
              component="img"
              src={appStoreImg}
              alt="Download on the App Store"
              sx={{
                width: { xs: 120, sm: 130, md: 150 },
                maxWidth: "calc(50% - 8px)",
                height: "auto",
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>

        {/* Column 2 — Company */}
        <Box sx={gridAreaSx.company}>
          <Typography variant="subtitle1" sx={columnTitleSx}>
            Company
          </Typography>
          {[
            ["About Us", goToAbout],
            ["Terms and Conditions", goToTermsConditions],
            ["Privacy Policy", goToprivacypolicy],
            ["Vendor Agreement", goToVendorAgreement],
          ].map(([label, onClick]) => (
            <Link
              key={label}
              onClick={onClick}
              color="inherit"
              underline="none"
              sx={columnLinkSx}
            >
              {label}
            </Link>
          ))}
        </Box>

        {/* Column 3 — Help Center */}
        <Box sx={gridAreaSx.help}>
          <Typography variant="subtitle1" sx={columnTitleSx}>
            Help Center
          </Typography>
          {["Order Tracking", "Returns & Refunds", "Shipping Information"].map(
            (item) => (
              <Link
                key={item}
                color="inherit"
                underline="none"
                sx={columnLinkSx}
                onClick={(e) => {
                  e.preventDefault();
                  handleAccountClick(item);
                }}
              >
                {item}
              </Link>
            ),
          )}
        </Box>

        {/* Column 4 — My Account */}
        <Box sx={gridAreaSx.account}>
          <Typography variant="subtitle1" sx={columnTitleSx}>
            My Account
          </Typography>
          {["Wishlist", "Gift cards", "Deals & Offers"].map((item) => (
            <Link
              href="#"
              key={item}
              color="inherit"
              underline="none"
              sx={columnLinkSx}
              onClick={(e) => {
                e.preventDefault();
                handleAccountClick(item);
              }}
            >
              {item}
            </Link>
          ))}
        </Box>
      </Box>

      {/* Footer bottom */}
      <Box
        sx={{
          borderTop: "1px solid rgba(255, 255, 255, 0.5)",
          marginTop: { xs: 2, sm: 2.5, md: 3 },
          paddingTop: { xs: 2, sm: 2.5, md: 3 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "center", sm: "center" },
          gap: { xs: 1.5, sm: 1 },
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        {/* <Link
          href="#"
          color="inherit"
          underline="none"
          sx={{
            fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
            wordBreak: "break-word",
          }}
        >
          Privacy Policy and Terms & Conditions
        </Link> */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              marginRight: { xs: 0.5, sm: 1 },
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
              display: "inline",
            }}
          >
            Follow us:
          </Typography>
          <FacebookIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
          <TwitterIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
          <InstagramIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
